import { useState } from 'react';
import styles from './history-panel.module.css';
import { useUIStore } from '@/stores/use-ui-store';
import { useActiveEntityId, useNavigateToEntity } from '@/hooks/navigation';
import { useCanEdit } from '@/lib/sharing/hooks/use-can-edit';
import { useSnapshotService } from '@/lib/snapshot/use-snapshot-service';
import { useDocumentSnapshots } from '@/lib/sync/hooks/use-document-snapshots';
import type { SnapshotInfo } from '@/lib/sync/entity-store';
import { useWorkspaceDocs } from '@/lib/sync/hooks/use-workspace-docs';
import { Flex, Text, IconButton, Button, Popconfirm, Tag, Timeline, message, Empty, Icon, Input, Tooltip } from '@nula/core-ui';
import { SidePanel } from '@/components/side-panel/side-panel';

function formatTimestamp(ts: string): string {
  const ms = parseInt(ts, 10);
  const date = !isNaN(ms) && String(ms) === ts ? new Date(ms) : new Date(ts);
  if (isNaN(date.getTime())) return ts;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin} min${diffMin === 1 ? '' : 's'} ago`;
  if (diffHr < 24) return `${diffHr} hr${diffHr === 1 ? '' : 's'} ago`;

  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const timeStr = new Intl.DateTimeFormat(navigator.language, { hour: 'numeric', minute: '2-digit' }).format(date);
  if (isToday) return `Today ${timeStr}`;
  if (isYesterday) return `Yesterday ${timeStr}`;
  return new Intl.DateTimeFormat(navigator.language, { month: 'short', day: 'numeric' }).format(date) + ` ${timeStr}`;
}

export { formatTimestamp };

// ── HistoryPanelInner — rendered only when activeEntityId is present ───────

function HistoryPanelInner({ entityId }: { entityId: string }) {
  const service = useSnapshotService();
  const snapshots = useDocumentSnapshots(entityId);
  const navigateToEntity = useNavigateToEntity();
  const allDocs = useWorkspaceDocs();
  const setDiffView = useUIStore((s) => s.setDiffView);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSelect = (snap: SnapshotInfo) => {
    setSelectedSnapshotId(snap.id);
    const doc = allDocs.find((d) => d.id === entityId);
    // Diff is entity-scoped. Canvas/space route to the canvas viewer;
    // everything else is a plain document.
    const diffKind = doc?.kind === 'canvas' ? 'canvas' : 'document';
    setDiffView({
      mode: 'history',
      entityId,
      entityKind: diffKind,
      snapshotId: snap.id,
      timestamp: snap.timestamp,
      name: snap.name ?? undefined,
    });
    navigateToEntity(entityId);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await service.createSnapshot(entityId, saveName.trim() || undefined);
      setSaveName('');
      message.success('Snapshot saved');
    } catch {
      message.error('Failed to save snapshot');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOne = async (snapshotId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedSnapshotId === snapshotId) setSelectedSnapshotId(null);
    try {
      await service.deleteOneSnapshot(entityId, snapshotId);
      message.success('Snapshot deleted');
    } catch {
      message.error('Failed to delete snapshot');
    }
  };

  const handleDeleteAll = async () => {
    setSelectedSnapshotId(null);
    try {
      await service.deleteAllSnapshots(entityId);
      message.success('History cleared');
    } catch {
      message.error('Failed to clear history');
    }
  };

  const handleClose = () => {
    useUIStore.getState().setVersionHistoryOpen(false);
  };

  const saveArea = (
    <Input
      label="Save snapshot…"
      value={saveName}
      variant="glass"
      onChange={(e) => setSaveName(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') void handleSave(); }}
      className={styles.saveInputSize}
      action={
        <Tooltip title="Save snapshot" placement="top">
          <IconButton
            size="sm"
            aria-label="Save snapshot"
            disabled={saving}
            onClick={() => { void handleSave(); }}
            className={styles.saveButton}
          >
            <Icon name="save" size={14} aria-hidden="true" />
          </IconButton>
        </Tooltip>
      }
    />
  );

  const footer = snapshots.length > 0 ? (
    <Popconfirm
      title="Delete all version history?"
      okText="Delete all"
      okDanger
      onConfirm={handleDeleteAll}
    >
      <Button
        size="lg"
        variant="text"
        block
        intent="danger"
        icon={<Icon name="delete" size={16} weight={300} />}
        className={styles.clearButton}
      >
        Clear all
      </Button>
    </Popconfirm>
  ) : undefined;

  return (
    <SidePanel title="Version History" icon="history" onClose={handleClose} titleExtra={undefined} footer={footer}>
      {saveArea}

      {snapshots.length === 0 && (
        <Empty
          image={<Icon name="history" size={48} className={styles.emptyIcon} />}
          description="No snapshots yet. History is created automatically as you edit."
          className={styles.emptyState}
        />
      )}

      {snapshots.length > 0 && (
        <Timeline
          items={snapshots.map((snap) => {
            const isActive = selectedSnapshotId === snap.id;
            return {
              label: formatTimestamp(snap.timestamp),
              color: isActive
                ? 'var(--accent)'
                : snap.trigger === 'manual'
                  ? 'var(--success)'
                  : undefined,
              children: (
                <Flex
                  align="center"
                  justify="space-between"
                  onClick={() => handleSelect(snap)}
                  className={styles.snapshotRow}
                >
                  <Flex vertical gap="xs" className={styles.snapshotContent}>
                    {snap.name && (
                      <Text size="sm" strong className={styles.snapshotName}>
                        {snap.name}
                      </Text>
                    )}
                    <Tag
                      color={snap.trigger === 'manual' ? 'success' : 'default'}
                      bordered={false}
                    >
                      {snap.trigger}
                    </Tag>
                  </Flex>
                  <IconButton
                    size="sm"
                    hoverBackground="color-mix(in srgb, var(--error) 15%, transparent)"
                    aria-label="Delete snapshot"
                    onClick={(e) => handleDeleteOne(snap.id, e)}
                  >
                    <Icon name="delete" size={10} />
                  </IconButton>
                </Flex>
              ),
            };
          })}
        />
      )}
    </SidePanel>
  );
}

// ── HistoryPanel — outer shell handles missing entityId ─────────────────────────

export function HistoryPanel() {
  const activeEntityId = useActiveEntityId();
  const canEdit = useCanEdit(activeEntityId ?? null);

  if (!activeEntityId || !canEdit) {
    return (
      <SidePanel
        title="Version History"
        icon="history"
        onClose={() => useUIStore.getState().setVersionHistoryOpen(false)}
      >
        <Text type="secondary" size="xs" className={styles.noDocText}>
          {activeEntityId ? 'Version history is available to editors only.' : 'No document open.'}
        </Text>
      </SidePanel>
    );
  }

  return <HistoryPanelInner entityId={activeEntityId} />;
}
